// One person's answer as it is kept: the wording they were shown, details filled in, and what
// the app sent with it.

import Modal from '../Modal';
import { InfoDot } from '../hiring/HiringForm';
import { CONSENT_RECORD_NOTE, SIGN_IN_NOTE, signInWords } from './bannerOptions';
import type { TrackerPerson } from './bannerApi';

const ANSWER_WORD: Record<string, string> = { agreed: 'Agreed', declined: 'Declined', got_it: 'Pressed the button' };

/** India time, as the rest of the tracker's header. */
const whenInIndia = (iso: string): string =>
  `${new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} IST`;

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 whitespace-pre-wrap break-words text-sm text-gray-900">{children}</dd>
    </div>
  );
}

export function AnswerWordingModal({ person, onClose }: { person: TrackerPerson; onClose: () => void }) {
  const { wording, answer, answeredAt, appVersion, platform, callerProof } = person;
  const who = person.name ? `${person.name} (${person.empCode})` : person.empCode;
  return (
    <Modal isOpen onClose={onClose} title={`What ${who} was shown`}>
      {wording && (
        <dl className="space-y-3">
          <Row label="Title">{wording.title}</Row>
          <Row label="Message">{wording.message}</Row>
          <Row label="Buttons">{[wording.primaryLabel, wording.secondaryLabel].filter(Boolean).join(' · ')}</Row>
          {answer && (
            <Row label="Answer">
              {ANSWER_WORD[answer] ?? answer}
              {answeredAt && `, ${whenInIndia(answeredAt)}`}
            </Row>
          )}
          <Row label="App">{[appVersion, platform].filter(Boolean).join(' · ') || 'Not sent'}</Row>
          <Row label="Sign-in">
            <span className="inline-flex items-center gap-1">
              {signInWords(callerProof)}
              {callerProof === 'app-pass' && <InfoDot text={SIGN_IN_NOTE} />}
            </span>
          </Row>
          {answer !== 'got_it' && <p className="text-xs text-gray-500">{CONSENT_RECORD_NOTE}</p>}
        </dl>
      )}
    </Modal>
  );
}
